import PlainDate from "@testout/testout-commerce/models/PlainDate";
import { msalInstance } from "./index";
import { loginRequest } from "./authConfig";
import { AuthenticationResult, InteractionRequiredAuthError } from "@azure/msal-browser";

export default class Api {

    static async getAccessToken(): Promise<string> {
        const account = msalInstance.getActiveAccount() ?? undefined;
        let tokenResponse: AuthenticationResult|undefined;
        try {
            tokenResponse = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
        }
        catch (ex: any) {
            if (ex instanceof InteractionRequiredAuthError)
                tokenResponse = await msalInstance.acquireTokenPopup({ ...loginRequest, account });
        }
        return tokenResponse?.accessToken ?? "";
    }

    /** Create an object.
     * @param uri The URI of the collection where the object should be created.
     * @param obj The object to create.
     * @return (200) The created object; (4xx/5xx) an error object.
     */
    static async post<T = any, B = any>(uri: string, obj?: B): Promise<T> {
        return Api.fetch<T>(uri, "POST", obj);
    }

    /** Retrieve an object or collection of objects.
     * @param uri The URI of the object or collection to be retrieved.
     * @param abortSignal An optional signal for aborting retrieval in progress.
     * @return (200) The object or collection of objects; (4xx/5xx) an error object.
     */ 
    static async get<T = any>(uri: string, abortSignal?: AbortSignal): Promise<T> {
        return Api.fetch<T>(uri, "GET", undefined, abortSignal);
    }

    /** Retrieve a typed object.
     * @param Type The constructor to use when instantiating the object.
     * @param uri The URI of the object or collection to be retrieved.
     * @param abortSignal An optional signal for aborting retrieval in progress.
     * @return (200) The typed object; (4xx/5xx) an error object.
     */ 
    static async getObject<T = {}, T2 = {}>(Type: (new(obj?: T2) => T), uri: string, abortSignal?: AbortSignal): Promise<T> {
        return new Type(await Api.fetch<T2>(uri, "GET", undefined, abortSignal));
    }

    /** Update or replace an object.
     * @param uri The URI of the object to update or replace.
     * @param obj The new object.
     * @return (200) The updated object; (4xx/5xx) an error object.
     */ 
    static async put<T = any, B = any>(uri: string, obj: B): Promise<T> {
        return Api.fetch<T>(uri, "PUT", obj);
    }

    /** Update or modify an object.
     * @param uri The URI of the object to update or modify.
     * @param obj The object with properties to update.
     * @return (200) The updated object; (4xx/5xx) an error object.
     */ 
    static async patch<T = any, B = any>(uri: string, obj: B): Promise<T> {
        return Api.fetch<T>(uri, "PATCH", obj);
    }

    /** Delete an object.
     * @param url The URL of the object to delete.
     * @return (4xx/5xx) an error object.
     */
    static async delete<T = any>(uri: string): Promise<T> {
        return Api.fetch<T>(uri, "DELETE");
    }

    /** Perform a RESTful method on the application server.
     * @param uri The URI of an object or collection.
     * @param method The method to perform.
     * @param obj The object on which to perform the method.
     * @param abortSignal An optional signal for aborting the method in progress.
     * @return Optionally an object or error object.
     */
    static async fetch<T = any, B = any>(uri: string, method: string, obj?: B, abortSignal?: AbortSignal): Promise<T> {
        try {
            const accessToken = await Api.getAccessToken();

            const response = await fetch(uri,
                {
                    method: method,
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: obj ? JSON.stringify(obj) : undefined,
                    signal: abortSignal
                });
            if (response.ok) {
                const data = await response.text();
                return data.length > 0 ? JSON.parse(data, PlainDate.reviver) : undefined;
            } else if (response.headers.get("Content-Type")?.startsWith("application/problem+json")) {
                const problem: Problem = await response.json() as Problem;
                let error = `[${problem.status || response.status}]`;
                if (problem.title) error += ` ${problem.title}`;
                if (problem.detail) error += ` ${problem.detail}`;

                for (var message of Object.values(problem.errors || {}))
                    error += ` ${message}`;
                throw Error(error);
            } else
            {
                const data = await response.text();
                const error = data.length > 0 && data.startsWith("{") ? (JSON.parse(data).detail || "") : "";
                throw Error(`[${response.status} ${response.statusText}] ${error}`);
            }
        }
        catch (error: any) {
            throw new Error(`API "${method} ${uri}" failed. ${Api.shorten(error.message)}`);
        }
    }

    static shorten(message: string): string {
        // Remove the stack trace if it is there
        var truncate = message.indexOf('\n   at ');
        if (truncate > 0)
            message = message.substring(0, truncate - 1);
        return message;
    } 
}

interface Problem {
    type?: string;
    title?: string;
    status?: number;
    detail?: string;
    instance?: string;
    errors?: { [key: string]: string }
}